import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { PageHeader } from "@/components/patterns";
import { GripVertical, Loader2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

type Stage = {
  id: string;
  name: string;
  order_index: number;
  probability_percent: number;
  sla_days: number | null;
  color_code: string;
  is_won_stage: boolean;
  is_lost_stage: boolean;
  is_standard: boolean;
  deal_count: number;
  needs: string[];
  required_rules: string[];
};
type Pipe = { id: string; name: string; code: string; description?: string; is_default: boolean; deal_count: number; stages: Stage[] };
type LostReason = { id: string; name: string; code: string; count: number };
type Rules = { next_step_required: boolean; allow_stage_skip: boolean; discount_approval_enabled: boolean; discount_approval_pct: number };
type Overview = { pipelines: Pipe[]; lost_reasons: LostReason[]; rules: Rules; requirement_options: { key: string; label: string }[] };

/** A stage row being edited: new rows have no saved copy yet */
type Row = Stage & { fresh?: boolean };

const isFixed = (s: Stage) => s.is_won_stage || s.is_lost_stage;
const NEW_STAGE_COLOURS = ["#A9BCE8", "#7F9BDD", "#4E74CF", "#2A57C0", "#1E4FB4"];

/**
 * The stages as they will be saved. Until someone reorders or adds a stage the saved order values are
 * kept as they are; after that the movable stages are numbered in the chosen order, then Won and Lost.
 */
function numbered(rows: Row[], reordered: boolean): Row[] {
  if (!reordered && !rows.some((r) => r.fresh)) return rows;
  const ordered = [...rows.filter((r) => !isFixed(r)), ...rows.filter(isFixed)];
  return ordered.map((r, i) => ({ ...r, order_index: i + 1 }));
}

const editable = (r: Row) => JSON.stringify([r.name, r.probability_percent, r.sla_days, r.color_code, r.order_index, [...r.required_rules].sort()]);

export function PipelinePage() {
  const [data, setData] = useState<Overview | null>(null);
  const [activeId, setActiveId] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [reordered, setReordered] = useState(false);
  const [newPipeline, setNewPipeline] = useState<{ name: string; code: string } | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ruleMenu, setRuleMenu] = useState<string | null>(null);
  const [ruleAnchor, setRuleAnchor] = useState<HTMLElement | null>(null);
  const [discountPct, setDiscountPct] = useState("10");
  const { success, error } = useToast();

  const load = async (keep?: string) => {
    try {
      const res = unwrapObject<Overview>(await api.get("/api/Pipeline/Overview"));
      setData(res);
      setDiscountPct(String(res.rules.discount_approval_pct));
      const id = keep && res.pipelines.some((p) => p.id === keep) ? keep : res.pipelines.find((p) => p.is_default)?.id || res.pipelines[0]?.id || "";
      setActiveId(id);
      resetRows(res.pipelines.find((p) => p.id === id));
    } catch (err: any) {
      error("Could not load pipelines", err.message || "Please try again");
    }
  };
  const resetRows = (p?: Pipe) => {
    setRows(p ? p.stages.map((s) => ({ ...s })) : []);
    setRemoved([]);
    setReordered(false);
  };
  useEffect(() => {
    load();
  }, []);

  const pipe = data?.pipelines.find((p) => p.id === activeId);
  const saved = useMemo(() => new Map((pipe?.stages || []).map((s) => [s.id, s])), [pipe]);

  const target = numbered(rows, reordered);
  const changes = {
    added: target.filter((r) => r.fresh),
    updated: target.filter((r) => !r.fresh && saved.get(r.id) && editable(r) !== editable(saved.get(r.id) as Row)),
    removed,
  };
  const changeCount = changes.added.length + changes.updated.length + changes.removed.length;
  const invalid = target.some((r) => !r.name.trim() || r.probability_percent < 0 || r.probability_percent > 100 || (r.sla_days !== null && r.sla_days < 1));

  const choose = (id: string) => {
    if (changeCount > 0 && !window.confirm("Discard your unsaved stage changes?")) return;
    setActiveId(id);
    resetRows(data?.pipelines.find((p) => p.id === id));
  };

  const patch = (id: string, change: Partial<Row>) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...change } : r)));

  const addStage = () =>
    setRows((rs) => [
      ...rs,
      {
        id: `new-${Date.now()}`,
        fresh: true,
        name: "New stage",
        order_index: rs.length + 1,
        probability_percent: 50,
        sla_days: null,
        color_code: NEW_STAGE_COLOURS[rs.filter((r) => !isFixed(r)).length % NEW_STAGE_COLOURS.length] as string,
        is_won_stage: false,
        is_lost_stage: false,
        is_standard: false,
        deal_count: 0,
        needs: [],
        required_rules: [],
      },
    ]);

  const removeStage = (r: Row) => {
    if (!r.fresh) setRemoved((x) => [...x, r.id]);
    setRows((rs) => rs.filter((x) => x.id !== r.id));
  };

  const dropOn = (overId: string) => {
    if (!dragId || dragId === overId) return;
    setRows((rs) => {
      const from = rs.findIndex((r) => r.id === dragId);
      const to = rs.findIndex((r) => r.id === overId);
      if (from < 0 || to < 0 || isFixed(rs[to] as Row)) return rs;
      const next = [...rs];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved as Row);
      setReordered(true);
      return next;
    });
  };

  const save = async () => {
    if (!pipe) return;
    setSaving(true);
    try {
      for (const id of changes.removed) await api.delete(`/api/Pipeline/DeleteStage/${id}`);
      for (const r of changes.updated) {
        await api.put(`/api/Pipeline/UpdateStage/${r.id}`, {
          pipeline_id: pipe.id,
          name: r.name.trim(),
          probability_percent: r.probability_percent,
          sla_days: r.sla_days,
          color_code: r.color_code,
          order_index: r.order_index,
          required_rules: r.required_rules,
        });
      }
      for (const r of changes.added) {
        await api.post("/api/Pipeline/AddStage", {
          pipeline_id: pipe.id,
          name: r.name.trim(),
          probability_percent: r.probability_percent,
          sla_days: r.sla_days,
          color_code: r.color_code,
          order_index: r.order_index,
          required_rules: r.required_rules,
        });
      }
      success("Pipeline saved", "Boards and forecasts use the new stages straight away");
      await load(pipe.id);
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
      await load(pipe.id);
    } finally {
      setSaving(false);
    }
  };

  const createPipeline = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPipeline?.name.trim() || !newPipeline.code.trim()) return error("Validation Error", "Name and code are required");
    setBusy(true);
    try {
      await api.post("/api/Pipeline/Insert", { name: newPipeline.name.trim(), code: newPipeline.code.trim().toUpperCase() });
      success("Pipeline created", "Add its stages, then save");
      setNewPipeline(null);
      await load();
    } catch (err: any) {
      error("Could not create", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const makeDefault = async (p: Pipe) => {
    setBusy(true);
    try {
      await api.put(`/api/Pipeline/Update/${p.id}`, { name: p.name, code: p.code, is_default: true });
      success("Default changed", `New deals start in ${p.name}`);
      await load(p.id);
    } catch (err: any) {
      error("Could not change", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const deletePipeline = async (p: Pipe) => {
    if (!window.confirm(`Delete the pipeline “${p.name}”? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/api/Pipeline/Delete/${p.id}`);
      success("Pipeline deleted", p.name);
      await load();
    } catch (err: any) {
      error("Could not delete", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  /** Pipeline rules save the moment they change, since they apply to the whole company */
  const changeRule = async (change: Partial<Rules>) => {
    setBusy(true);
    try {
      const rules = unwrapObject<Rules>(await api.put("/api/Pipeline/Rules", change));
      setData((d) => (d ? { ...d, rules } : d));
      setDiscountPct(String(rules.discount_approval_pct));
      success("Rule saved", "It applies from the next move or quote");
    } catch (err: any) {
      error("Could not save the rule", err.message || "Please try again");
      setDiscountPct(String(data?.rules.discount_approval_pct ?? 10));
    } finally {
      setBusy(false);
    }
  };

  const toggleRequirement = (id: string, key: string) => {
    const r = rows.find((x) => x.id === id);
    if (!r) return;
    patch(id, { required_rules: r.required_rules.includes(key) ? r.required_rules.filter((k) => k !== key) : [...r.required_rules, key] });
  };

  const addReason = async () => {
    const name = (reason || "").trim();
    if (!name) return setReason(null);
    setBusy(true);
    try {
      await api.post("/api/MasterLookup/Insert", {
        category: "DEAL_LOST_REASON",
        name,
        code: name.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40),
        sort_order: (data?.lost_reasons.length || 0) + 1,
      });
      setReason(null);
      await load(activeId);
    } catch (err: any) {
      error("Could not add", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const num = "h-7 rounded-md border border-input bg-card px-2 text-right font-mono text-xs";
  const numStyle = { width: 72 } as const;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Pipelines & stages"
        actions={
          <>
            {changeCount > 0 && <span className="text-xs font-medium text-warning">{changeCount} unsaved change{changeCount === 1 ? "" : "s"}</span>}
            {changeCount > 0 && (
              <Button variant="outline" size="sm" onClick={() => resetRows(pipe)} disabled={saving}>
                Discard
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setNewPipeline({ name: "", code: "" })}>
              + New pipeline
            </Button>
            <Button size="sm" onClick={save} disabled={saving || changeCount === 0 || invalid}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : "Save"}
            </Button>
          </>
        }
      />

      <p className="text-[13px] text-muted-foreground">The steps every deal goes through. Probability feeds the forecast; the day limit flags stalled deals.</p>

      {!data || !pipe ? (
        <TableSkeleton rows={6} columns={5} />
      ) : (
        <>
          <div role="tablist" aria-label="Pipeline" className="flex flex-wrap items-center gap-0.5 border-b border-border">
            {data.pipelines.map((p) => (
              <button
                key={p.id}
                role="tab"
                type="button"
                aria-selected={p.id === activeId}
                onClick={() => choose(p.id)}
                className={cn("h-[34px] border-b-2 px-3 text-[13px]", p.id === activeId ? "border-primary font-semibold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}
              >
                {p.name}
                <span className={cn("ml-1.5 font-mono text-[11px]", p.id === activeId && "rounded-full bg-accent px-1.5 text-primary")}>{p.deal_count} deals</span>
              </button>
            ))}
            <div className="flex-1" />
            {pipe.is_default ? (
              <span className="px-2 text-xs text-muted-foreground">Default pipeline</span>
            ) : (
              <>
                <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={busy} onClick={() => makeDefault(pipe)}>
                  Make default
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-danger" disabled={busy} onClick={() => deletePipeline(pipe)}>
                  Delete pipeline
                </Button>
              </>
            )}
          </div>

          <div className="flex flex-wrap items-start gap-3.5">
            <section aria-label="Stages" className="min-w-0 flex-[999_1_560px] overflow-x-auto rounded-[10px] border border-border bg-card">
              <table className="w-full min-w-[720px] border-collapse text-[12.5px]">
                <thead>
                  <tr className="bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="h-[34px] w-8 border-b border-border">
                      <span className="sr-only">Reorder</span>
                    </th>
                    <th className="border-b border-border px-2.5 font-semibold">Stage</th>
                    <th className="w-[110px] border-b border-border px-2.5 font-semibold">Probability</th>
                    <th className="w-[130px] border-b border-border px-2.5 font-semibold">Stalled after</th>
                    <th className="border-b border-border px-2.5 font-semibold">Needed to enter</th>
                    <th className="border-b border-border px-3.5 text-right font-semibold">Deals now</th>
                  </tr>
                </thead>
                <tbody>
                  {target.map((r) => {
                    const fixed = isFixed(r);
                    const closes = r.is_won_stage || r.is_lost_stage;
                    return (
                      <tr
                        key={r.id}
                        draggable={!fixed}
                        onDragStart={() => setDragId(r.id)}
                        onDragOver={(e) => !fixed && e.preventDefault()}
                        onDrop={() => dropOn(r.id)}
                        onDragEnd={() => setDragId(null)}
                        className={cn(fixed && "bg-muted/30", dragId === r.id && "opacity-50")}
                      >
                        <td className="border-b border-border/60 text-center text-muted-foreground">
                          {!fixed && <GripVertical className="mx-auto size-4 cursor-grab" aria-label={`Drag to reorder ${r.name}`} />}
                        </td>
                        <td className="border-b border-border/60 px-2.5 py-1.5">
                          <span className="flex items-center gap-2">
                            <label className="relative size-3 shrink-0 cursor-pointer rounded-[3px]" style={{ background: r.color_code }} title="Stage colour">
                              <input type="color" value={r.color_code} onChange={(e) => patch(r.id, { color_code: e.target.value })} aria-label={`Colour of ${r.name}`} className="absolute inset-0 size-full cursor-pointer opacity-0" />
                            </label>
                            <input
                              type="text"
                              value={r.name}
                              onChange={(e) => patch(r.id, { name: e.target.value })}
                              aria-label="Stage name"
                              className={cn("h-[30px] flex-1 rounded-md border border-transparent bg-transparent px-1.5 font-medium hover:border-input focus:border-ring focus:outline-none", !r.name.trim() && "border-danger")}
                            />
                            {r.fresh && <span className="rounded bg-accent px-1.5 text-[10.5px] font-semibold text-primary">new</span>}
                          </span>
                        </td>
                        <td className="border-b border-border/60 px-2.5">
                          <span className="inline-flex items-center gap-1">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={r.probability_percent}
                              disabled={closes}
                              onChange={(e) => patch(r.id, { probability_percent: Number(e.target.value) })}
                              aria-label={`Probability for ${r.name}`}
                              style={numStyle}
                              className={cn(num, closes && "opacity-60")}
                            />
                            %
                          </span>
                        </td>
                        <td className="border-b border-border/60 px-2.5">
                          {closes ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <input
                                type="number"
                                min={1}
                                placeholder="never"
                                value={r.sla_days ?? ""}
                                onChange={(e) => patch(r.id, { sla_days: e.target.value === "" ? null : Number(e.target.value) })}
                                aria-label={`Stalled after, in days, for ${r.name}`}
                                style={numStyle}
                                className={num}
                              />
                              days
                            </span>
                          )}
                        </td>
                        <td className="border-b border-border/60 px-2.5">
                          <span className="flex flex-wrap items-center gap-1">
                            {r.needs.filter((n) => !(saved.get(r.id)?.required_rules || []).some((k) => data.requirement_options.find((o) => o.key === k)?.label === n)).map((n) => (
                              <span key={n} className="rounded-[5px] bg-muted px-[7px] text-[11.5px] leading-[22px] text-foreground">
                                {n}
                              </span>
                            ))}
                            {r.required_rules.map((k) => (
                              <span key={k} className="inline-flex items-center gap-1 rounded-[5px] bg-accent px-[7px] text-[11.5px] leading-[22px] text-accent-foreground">
                                {data.requirement_options.find((o) => o.key === k)?.label || k}
                                <button type="button" onClick={() => toggleRequirement(r.id, k)} aria-label={`Remove requirement ${k} from ${r.name}`} className="text-muted-foreground hover:text-danger">
                                  ×
                                </button>
                              </span>
                            ))}
                            {r.needs.length === 0 && r.required_rules.length === 0 && <span className="text-muted-foreground">Nothing</span>}
                            {!r.is_lost_stage && !r.is_won_stage && (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    setRuleAnchor(e.currentTarget);
                                    setRuleMenu(ruleMenu === r.id ? null : r.id);
                                  }}
                                  aria-haspopup="menu"
                                  aria-expanded={ruleMenu === r.id}
                                  className="h-[22px] px-1.5 text-[11.5px] text-primary hover:underline"
                                >
                                  + rule
                                </button>
                                {ruleMenu === r.id && ruleAnchor && (
                                  <RuleMenu anchor={ruleAnchor} options={data.requirement_options} chosen={r.required_rules} onToggle={(k) => toggleRequirement(r.id, k)} onClose={() => setRuleMenu(null)} />
                                )}
                              </>
                            )}
                          </span>
                        </td>
                        <td className="border-b border-border/60 px-3.5 text-right font-mono text-muted-foreground">
                          {closes ? (
                            "—"
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              {r.deal_count}
                              {!r.is_standard && (
                                <button type="button" onClick={() => removeStage(r)} aria-label={`Remove ${r.name}`} title={r.deal_count ? "Move its deals first" : "Remove stage"} className="text-muted-foreground hover:text-danger">
                                  <Trash2 className="size-3.5" />
                                </button>
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5">
                <button type="button" onClick={addStage} className="text-[13px] font-medium text-primary hover:underline">
                  + Add stage
                </button>
                <span className="text-xs text-muted-foreground">Won and Lost always close the pipeline and cannot be moved. Standard stages can be renamed and re-timed, not removed. “+ rule” adds a requirement to enter a stage.</span>
              </div>
            </section>

            <aside className="flex max-w-[340px] flex-[1_1_280px] flex-col gap-3">
              <section className="flex flex-col gap-2 rounded-[10px] border border-border bg-card px-3.5 py-3 text-[13px]">
                <h2 className="text-sm font-semibold text-foreground">Pipeline rules</h2>
                <span className="text-xs text-muted-foreground">Apply to every pipeline. Saved as soon as you change them.</span>
                <label className="flex items-start gap-2">
                  <input type="checkbox" checked={data.rules.next_step_required} disabled={busy} onChange={(e) => changeRule({ next_step_required: e.target.checked })} className="mt-[3px] size-4 accent-primary" />
                  <span>
                    Ask for a next step when moving a deal forward
                    <span className="block text-xs text-muted-foreground">The deal needs an open task first.</span>
                  </span>
                </label>
                <div className="flex items-start gap-2">
                  <input id="discount-rule" type="checkbox" checked={data.rules.discount_approval_enabled} disabled={busy} onChange={(e) => changeRule({ discount_approval_enabled: e.target.checked })} className="mt-[3px] size-4 accent-primary" />
                  <label htmlFor="discount-rule" className="flex flex-wrap items-center gap-1">
                    Quotes above
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={discountPct}
                      onClick={(e) => e.preventDefault()}
                      onChange={(e) => setDiscountPct(e.target.value)}
                      onBlur={() => Number(discountPct) !== data.rules.discount_approval_pct && changeRule({ discount_approval_pct: Number(discountPct) })}
                      aria-label="Discount percentage that needs approval"
                      className="h-6 w-14 rounded-md border border-input bg-card px-1.5 text-right font-mono text-xs"
                    />
                    % discount need a sales head
                  </label>
                </div>
                <label className="flex items-start gap-2">
                  <input type="checkbox" checked={data.rules.allow_stage_skip} disabled={busy} onChange={(e) => changeRule({ allow_stage_skip: e.target.checked })} className="mt-[3px] size-4 accent-primary" />
                  <span>
                    Allow skipping stages
                    <span className="block text-xs text-muted-foreground">The stage being left must still be complete.</span>
                  </span>
                </label>
              </section>
              <section className="flex flex-col gap-1.5 rounded-[10px] border border-border bg-card px-3.5 py-3">
                <h2 className="text-sm font-semibold text-foreground">Lost reasons</h2>
                <span className="text-xs text-muted-foreground">Asked when a deal is marked lost; feeds the Lost analysis report.</span>
                {data.lost_reasons.map((r) => (
                  <div key={r.id} className="flex items-start justify-between gap-3 border-t border-border/60 py-1.5">
                    <span className="leading-5">{r.name}</span>
                    <span className="font-mono text-xs leading-5 text-muted-foreground">{r.count}</span>
                  </div>
                ))}
                {reason === null ? (
                  <button type="button" onClick={() => setReason("")} className="self-start py-1 text-xs text-primary hover:underline">
                    + Add reason
                  </button>
                ) : (
                  <div className="flex gap-1.5 pt-1">
                    <Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addReason()} placeholder="e.g. Lost to import" className="h-8 text-sm" aria-label="New lost reason" />
                    <Button size="sm" onClick={addReason} disabled={busy}>
                      Add
                    </Button>
                  </div>
                )}
              </section>
            </aside>
          </div>
        </>
      )}

      <Dialog open={!!newPipeline} onOpenChange={(o) => !o && setNewPipeline(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New pipeline</DialogTitle>
            <DialogDescription>For a different kind of sale, such as export orders or sampling. You add its stages next.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createPipeline} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={newPipeline?.name || ""} onChange={(e) => setNewPipeline((p) => p && { ...p, name: e.target.value, code: p.code || e.target.value.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "") })} placeholder="Export orders" />
            </div>
            <div className="space-y-1.5">
              <Label>Code</Label>
              <Input value={newPipeline?.code || ""} onChange={(e) => setNewPipeline((p) => p && { ...p, code: e.target.value.toUpperCase() })} placeholder="EXPORT_ORDERS" className="font-mono" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewPipeline(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The "+ rule" menu. It is drawn outside the table, positioned from the button, so the table's own
 * scrolling can never cut it off; it opens upward when there is no room below.
 */
function RuleMenu({
  anchor,
  options,
  chosen,
  onToggle,
  onClose,
}: {
  anchor: HTMLElement;
  options: { key: string; label: string }[];
  chosen: string[];
  onToggle: (key: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const h = ref.current?.offsetHeight || 190;
      const w = 248;
      const below = window.innerHeight - a.bottom;
      setPos({
        left: Math.max(8, Math.min(a.left, window.innerWidth - w - 8)),
        top: below >= h + 8 ? a.bottom + 4 : Math.max(8, a.top - h - 4),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchor]);

  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node) && !anchor.contains(e.target as Node)) onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label="Requirements to enter this stage"
      style={{ position: "fixed", left: pos?.left ?? -9999, top: pos?.top ?? -9999, width: 248, zIndex: 60 }}
      className="rounded-lg border border-border bg-card p-1 shadow-md"
    >
      <div className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Needed before a deal enters</div>
      {options.map((o) => (
        <label key={o.key} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] hover:bg-muted">
          <input type="checkbox" checked={chosen.includes(o.key)} onChange={() => onToggle(o.key)} className="size-4 accent-primary" />
          {o.label}
        </label>
      ))}
      <button type="button" onClick={onClose} className="mt-1 w-full rounded-md px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted">
        Done
      </button>
    </div>,
    document.body,
  );
}
